import { ArrowLeftOutlined, ExclamationCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { AutoComplete, Breadcrumb, Button, Card, Divider, Form, Input, InputNumber, message, Modal, Space, Typography } from "antd";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminAppLayout from "../../../Layouts/AdminLayout";
import { colors } from "../../../utils/colors";
import { RoutesLookup } from "../../../../Routes/RoutesLookup";
import { manualTransfer, searchAgents } from "./pointsTransferService";

const { TextArea } = Input;
const { Title } = Typography;

const PointsTransferCreate = () => {
  const [form] = Form.useForm();
  const [fromAgent, setFromAgent] = useState(null);
  const [toAgent, setToAgent] = useState(null);
  const [fromOptions, setFromOptions] = useState([]);
  const [toOptions, setToOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchingFrom, setSearchingFrom] = useState(false);
  const [searchingTo, setSearchingTo] = useState(false);
  const navigate = useNavigate();

  const handleSearchAgent = async (value, setOptions, setLoading) => {
    setLoading(true);
    try {
      const agents = await searchAgents(value);
      setOptions(
        (Array.isArray(agents) ? agents : agents || []).map((a) => ({
          value: `${a.Name} (${a.Email})`,
          label: (
            <div>
              <b>{a.Name}</b> <br />
              <small>{a.Email}</small>
              <br />
              <span style={{ color: '#888' }}>Points: {a.AvailablePoints}</span>
            </div>
          ),
          agent: a,
        }))
      );
    } catch (err) {
      setOptions([]);
    }
    setLoading(false);
  };

  const handleFromSelect = (value, option) => {
    const agent = option.agent;
    if (agent.AvailablePoints <= 0) {
      message.error("The selected agent does not have points available for transfer.");
      form.setFieldsValue({ fromAgent: undefined });
      setFromAgent(null);
      return;
    }
    setFromAgent(agent);
    form.setFieldsValue({ fromAgent: value });
  };

  const handleToSelect = (value, option) => {
    setToAgent(option.agent);
    form.setFieldsValue({ toAgent: value });
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      if (!fromAgent || !toAgent) {
        message.error("Please select both agents.");
        return;
      }
      if (fromAgent.KeyID === toAgent.KeyID) {
        message.error("Cannot transfer points to the same agent.");
        return;
      }
      if (!values.points || values.points <= 0) {
        message.error("Please enter a valid points amount.");
        return;
      }
      if (values.points > fromAgent.AvailablePoints) {
        message.error(`Transfer amount cannot exceed available points (${fromAgent.AvailablePoints}).`);
        return;
      }

      // Check if transfer would result in negative points
      const remainingPoints = fromAgent.AvailablePoints - values.points;
      if (remainingPoints < 0) {
        Modal.warning({
          title: 'Negative Points Warning',
          icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
          content: (
            <div>
              <p>This transfer would result in negative points for the sending agent:</p>
              <div style={{ 
                backgroundColor: '#fff7e6', 
                padding: '12px', 
                borderRadius: '6px', 
                border: '1px solid #ffd591',
                margin: '12px 0'
              }}>
                <div><strong>{fromAgent.Name}</strong></div>
                <div>Current Points: <strong>{fromAgent.AvailablePoints}</strong></div>
                <div>Transfer Amount: <strong>-{values.points}</strong></div>
                <div style={{ color: '#d46b08', fontWeight: 'bold' }}>
                  Resulting Balance: <strong>{remainingPoints}</strong>
                </div>
              </div>
              <p>Please verify this is intentional before proceeding.</p>
            </div>
          ),
          okText: 'I Understand',
          onOk() {
            // Continue with the transfer after warning acknowledgment
            performTransfer();
          }
        });
        return;
      }

      // Proceed with normal transfer
      await performTransfer();
    } catch (err) {
      message.error(err.message || "Failed to transfer points.");
    } finally {
      setLoading(false);
    }
  };

  const performTransfer = async () => {
    const values = form.getFieldsValue();
    await manualTransfer({
      fromAgentID: fromAgent.KeyID,
      toAgentID: toAgent.KeyID,
      points: values.points,
      notes: values.notes,
    });
    
    message.success("Points transferred successfully.");
    
    // Reset form and navigate back
    form.resetFields();
    setFromAgent(null);
    setToAgent(null);
    navigate(RoutesLookup.SETTINGS_POINTS_TRANSFER);
  };

  const handleCancel = () => {
    navigate(RoutesLookup.SETTINGS_POINTS_TRANSFER);
  };

  return (
    <AdminAppLayout>
      <div
       className="page-content-wrapper"
      >
        <Breadcrumb
          items={[
            { title: "Settings" },
            { title: "Points Transfer" },
            { title: "Manual Transfer" },
          ]}
        />
        <Title level={4} style={{ color: colors.black, marginTop: 3 }}>
          Manual Points Transfer
        </Title>
        <Divider type="horizontal" />
        
        <Space style={{ marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleCancel}
          >
            Back to Transfers
          </Button>
        </Space>

        <Card>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ maxWidth: 600 }}
          >
            <Form.Item
              label="From Agent"
              name="fromAgent"
              rules={[{ required: true, message: "Please select the agent to transfer from" }]}
            >
              <AutoComplete
                placeholder="Search and select agent"
                onSearch={(value) => handleSearchAgent(value, setFromOptions, setSearchingFrom)}
                onSelect={handleFromSelect}
                options={fromOptions}
                notFoundContent={searchingFrom ? <span>Loading...</span> : null}
                style={{ width: "100%" }}
              />
            </Form.Item>

            {fromAgent && (
              <div style={{ 
                backgroundColor: '#f0f8ff', 
                padding: '12px', 
                borderRadius: '6px', 
                marginBottom: '16px',
                border: '1px solid #d1ecf1'
              }}>
                <div style={{ fontWeight: 'bold', color: '#0c5460' }}>
                  Selected Agent: {fromAgent.Name}
                </div>
                <div style={{ fontSize: '13px', color: '#0c5460' }}>
                  Available Points: <strong>{fromAgent.AvailablePoints}</strong>
                </div>
              </div>
            )}

            <Form.Item
              label="To Agent"
              name="toAgent"
              rules={[{ required: true, message: "Please select the agent to transfer to" }]}
            >
              <AutoComplete
                placeholder="Search and select agent"
                onSearch={(value) => handleSearchAgent(value, setToOptions, setSearchingTo)}
                onSelect={handleToSelect}
                options={toOptions}
                notFoundContent={searchingTo ? <span>Loading...</span> : null}
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item
              label="Points to Transfer"
              name="points"
              rules={[
                { required: true, message: "Please enter the number of points to transfer" },
                { type: 'number', min: 0.01, message: "Points must be greater than 0" }
              ]}
            >
              <InputNumber
                placeholder="Enter points amount"
                style={{ width: "100%" }}
                min={0.01}
                step={0.01}
                precision={2}
                max={fromAgent?.AvailablePoints || undefined}
              />
            </Form.Item>

            <Form.Item
              label="Notes (Optional)"
              name="notes"
            >
              <TextArea
                placeholder="Add any notes about this transfer"
                rows={3}
              />
            </Form.Item>

            <Form.Item style={{ marginTop: 32 }}>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading} icon={<SaveOutlined />}>
                  Transfer Points
                </Button>
                <Button onClick={handleCancel}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </AdminAppLayout>
  );
};

export default PointsTransferCreate;